import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import InitialData from '../../InitialData';
import {
  DEFAULT_LOCALE,
  normalizeLocale,
  supportedLocales,
} from '../../locales/languages';

const HREFLANG_MAP = {
  en: 'en',
  el: 'el',
  de: 'de',
  sr: 'sr-Cyrl',
  ro: 'ro',
  fr: 'fr',
  it: 'it',
  bg: 'bg',
  mk: 'mk',
  ru: 'ru',
  uk: 'uk',
};

const CANONICAL_SITE_URL = (process.env.REACT_APP_SITE_URL || '').replace(
  /\/+$/,
  '',
);

const truncate = (value, maxLength = 160) => {
  if (!value || typeof value !== 'string') {
    return '';
  }
  if (value.length <= maxLength) {
    return value;
  }
  return `${value.slice(0, maxLength - 1).trim()}...`;
};

const isPlaceholderDescription = (value) => {
  if (!value || typeof value !== 'string') {
    return true;
  }
  const normalized = value.trim().toLowerCase();
  return (
    normalized === 'description in english' ||
    normalized === 'περιγραφή στα αγγλικά'
  );
};

const slugify = (value) => {
  if (!value) {
    return '';
  }
  return String(value)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-+/g, '-');
};

const findApartmentBySlug = (slug) => {
  return InitialData.find((apartment) => {
    const apartmentSlug = slugify(
      apartment.slug || apartment.name || apartment.longName || apartment.id,
    );
    return apartmentSlug === slug;
  });
};

const getRouteInfo = (pathname) => {
  const parts = pathname.split('/').filter(Boolean);
  const pathLocale = normalizeLocale(parts[0]);
  const routeParts = pathLocale ? parts.slice(1) : parts;

  if (routeParts[0] === 'rooms' && routeParts[1]) {
    const slug = routeParts[1];
    return {
      type: 'room',
      slug,
      apartment: findApartmentBySlug(slug),
    };
  }

  return { type: 'home' };
};

const buildLocalizedPath = (locale, routeInfo) => {
  if (routeInfo.type === 'room' && routeInfo.slug) {
    return `/${locale}/rooms/${routeInfo.slug}/`;
  }
  return `/${locale}/`;
};

const buildLocaleUrl = (origin, locale, routeInfo) => {
  return `${origin}${buildLocalizedPath(locale, routeInfo)}`;
};

const getAbsoluteUrl = (origin, value) => {
  if (!value) {
    return '';
  }
  return value.startsWith('http') ? value : `${origin}${value}`;
};

const upsertTag = (selector, tagName, attrs) => {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement(tagName);
    document.head.appendChild(tag);
  }
  Object.entries(attrs).forEach(([key, value]) => {
    tag.setAttribute(key, value);
  });
};

const upsertLink = (rel, href, hreflang) => {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]`;
  upsertTag(selector, 'link', { rel, href, ...(hreflang ? { hreflang } : {}) });
};

const upsertJsonLd = (id, data) => {
  let tag = document.head.querySelector(`script#${id}`);
  if (!tag) {
    tag = document.createElement('script');
    tag.id = id;
    tag.type = 'application/ld+json';
    document.head.appendChild(tag);
  }
  tag.textContent = JSON.stringify(data);
};

const removeJsonLd = (id) => {
  const tag = document.head.querySelector(`script#${id}`);
  if (tag) {
    tag.remove();
  }
};

const useSEO = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const origin = CANONICAL_SITE_URL || window.location.origin;
    const currentLocale = normalizeLocale(i18n.language) || DEFAULT_LOCALE;
    const routeInfo = getRouteInfo(location.pathname);
    const canonical = buildLocaleUrl(origin, currentLocale, routeInfo);
    const siteTitle = 'S. Luxury Princess Suite';
    const titleBase = `${siteTitle} | Nea Kallikratia, Chalkidiki`;
    const homeDescription =
      'Luxury suites in Nea Kallikratia, Chalkidiki. Sea view apartments, modern amenities, and comfortable stays near the Aegean Sea.';
    const { apartment } = routeInfo;
    const roomName = apartment?.longName || apartment?.name || '';
    const rawLocalizedDescription =
      typeof apartment?.desc === 'string'
        ? apartment.desc
        : apartment?.desc?.[currentLocale] || apartment?.desc?.[DEFAULT_LOCALE];
    const localizedDescription = isPlaceholderDescription(
      rawLocalizedDescription,
    )
      ? ''
      : rawLocalizedDescription;
    const title = apartment ? t('rooms.metaTitle', { roomName }) : titleBase;
    const roomDescription = apartment
      ? `${
          localizedDescription ||
          t('rooms.metaDescriptionFallback', { roomName })
        } ${t('rooms.directBookingLine')}`
      : '';
    const description = apartment ? truncate(roomDescription) : homeDescription;
    const socialImage = getAbsoluteUrl(
      origin,
      apartment?.photos?.[0] || '/b2-hero-1600.jpg',
    );

    const { documentElement } = document;
    documentElement.lang = currentLocale;
    document.title = title;

    upsertTag('meta[name="description"]', 'meta', {
      name: 'description',
      content: description,
    });

    upsertTag('meta[property="og:title"]', 'meta', {
      property: 'og:title',
      content: title,
    });
    upsertTag('meta[property="og:description"]', 'meta', {
      property: 'og:description',
      content: description,
    });
    upsertTag('meta[property="og:type"]', 'meta', {
      property: 'og:type',
      content: 'website',
    });
    upsertTag('meta[property="og:url"]', 'meta', {
      property: 'og:url',
      content: canonical,
    });
    upsertTag('meta[property="og:site_name"]', 'meta', {
      property: 'og:site_name',
      content: siteTitle,
    });
    if (socialImage) {
      upsertTag('meta[property="og:image"]', 'meta', {
        property: 'og:image',
        content: socialImage,
      });
    }

    upsertTag('meta[name="twitter:card"]', 'meta', {
      name: 'twitter:card',
      content: 'summary_large_image',
    });
    upsertTag('meta[name="twitter:title"]', 'meta', {
      name: 'twitter:title',
      content: title,
    });
    upsertTag('meta[name="twitter:description"]', 'meta', {
      name: 'twitter:description',
      content: description,
    });
    if (socialImage) {
      upsertTag('meta[name="twitter:image"]', 'meta', {
        name: 'twitter:image',
        content: socialImage,
      });
    }

    upsertLink('canonical', canonical);

    supportedLocales.forEach((locale) => {
      const href = buildLocaleUrl(origin, locale, routeInfo);
      const hreflang = HREFLANG_MAP[locale] || locale;
      upsertLink('alternate', href, hreflang);
    });

    upsertLink(
      'alternate',
      buildLocaleUrl(origin, DEFAULT_LOCALE, routeInfo),
      'x-default',
    );

    if (apartment) {
      upsertJsonLd('room-structured-data', {
        '@context': 'https://schema.org',
        '@type': 'Apartment',
        name: roomName,
        description,
        image: apartment.photos?.map((photo) => getAbsoluteUrl(origin, photo)),
        url: canonical,
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Katsirma 7',
          addressLocality: 'Nea Kallikratia',
          addressRegion: 'Chalkidiki',
          postalCode: '630 80',
          addressCountry: 'GR',
        },
      });
    } else {
      removeJsonLd('room-structured-data');
    }
  }, [i18n.language, location.pathname, t]);
};

export default useSEO;
